import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import backbutton from './assets/backbutton.png';
import SEO from './components/SEO';
import './css/PrivacyPolicy.css';

function PrivacyPolicy() {
  return (
    <div className="privacy-policy-container">
      <SEO 
        title="Privacy Policy - Savory"
        description="Read Savory's privacy policy to understand how we collect, use, and protect your personal information when using our recipe platform."
        keywords="privacy policy, data protection, user privacy, Savory terms, personal information"
        url="/privacy"
        type="article"
      />
      
      <div className="back-button-container">
        <Link to="/" className="back-button-details">
          <img src={backbutton} alt="Back" className="backButtonImg" />
        </Link>
      </div>

      <motion.div 
        className="privacy-content"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <motion.header 
          className="privacy-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h1>Privacy Policy</h1>
          <p className="effective-date">Effective Date: March 26, 2024</p>
        </motion.header>

        <motion.div 
          className="privacy-sections"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <section className="privacy-section">
            <h2>Introduction</h2>
            <p>
              Savory has developed its platform as a Freemium service to help users explore and cook recipes through video-to-recipe conversion and smart nutritional insights. This Privacy Policy explains how we collect, use, and disclose personal information from users of the Savory application and website.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Consent</h2>
            <p>
              By using Savory, you agree to the collection and use of your information as described in this Privacy Policy. We use your data to improve and personalize your experience, and we will not share your information except as outlined below.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Information Collection and Use</h2>
            <p>
              To provide key features — such as recipe tracking, personalized search, nutrition analysis, and user accounts — Savory may collect certain personal information, including but not limited to:
            </p>
            <ul>
              <li>Email address or login method (e.g., via Google)</li>
              <li>Nutrition preferences or filters</li>
              <li>Recipe views and saved items</li>
              <li>Any data submitted through user-generated content (e.g., uploaded videos or notes)</li>
            </ul>
            <p>
              This data is used solely to enhance your experience on Savory and to operate essential features of the service.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Log Data</h2>
            <p>
              In case of app or website errors, we may collect diagnostic information (Log Data) such as your:
            </p>
            <ul>
              <li>IP address</li>
              <li>Device name and type</li>
              <li>Operating system version</li>
              <li>Time and date of use</li>
              <li>App configuration and activity</li>
            </ul>
            <p>
              This data helps us troubleshoot and improve performance.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Cookies</h2>
            <p>
              Savory may use cookies or similar technologies to enhance your browsing experience. These may include session cookies, tracking cookies (via analytics tools), or third-party integrations. You can choose to disable cookies through your browser settings, though doing so may limit certain functionalities.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Third-Party Services</h2>
            <p>
              We may work with third-party service providers for:
            </p>
            <ul>
              <li>Authentication and account services</li>
              <li>Analytics and usage tracking</li>
              <li>Cloud storage and video processing</li>
              <li>Customer support or communication tools</li>
            </ul>
            <p>
              These providers may have access to limited personal data only to perform services on our behalf and are bound by strict confidentiality agreements.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Security</h2>
            <p>
              We take data protection seriously and implement industry-standard safeguards to secure your personal information. However, no method of digital storage or transmission is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Links to Other Sites</h2>
            <p>
              Savory may contain links to external websites (e.g., YouTube, content creators). We are not responsible for the privacy practices or content of those third-party sites. We recommend reviewing their privacy policies separately.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Children's Privacy</h2>
            <p>
              Savory is not designed for children under the age of 16. We do not knowingly collect personally identifiable information from minors. If you believe a child has submitted personal data to us, please contact us immediately.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy as our service evolves. Any changes will be posted on this page. We encourage you to review the policy periodically.
            </p>
          </section>

          <section className="privacy-section contact-section">
            <h2>Contact Us</h2>
            <p>
              If you have any questions or concerns about this Privacy Policy or how your information is used, please reach out to us at:
            </p>
            <p className="contact-email">
              📧 <a href="mailto:team@savory.ink">team@savory.ink</a>
            </p>
          </section>
        </motion.div>

        <motion.footer 
          className="privacy-footer"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <p>Last updated: March 26, 2024</p>
          <Link to="/" className="back-to-home">
            ← Back to Savory
          </Link>
        </motion.footer>
      </motion.div>
    </div>
  );
}

export default PrivacyPolicy;